import { Component, computed, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';

import { getPropsFrom, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';
import { DEFAULT_INITIAL_DATE } from '../../../../utils/dates';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { MatIcon } from '@angular/material/icon';
import {
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';

interface DateInputProperties {
  text: string;
  style: string;
  selectedDateTime: string;
  captionWidth: number;
  inputType: string;
}

@Component({
  selector: 'lfe-date-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './date-input-item-form.component.html',
  standalone: true,
  styleUrl: './date-input-item-form.component.css'
})
export class DateInputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<DateInputProperties>))
  implements OnInit, IBaseItem<DateInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  @Output() updateFirstColumnWidthRequired = new EventEmitter<{ id: string; width: number }>();

  translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & DateInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    selectedDateTime: '',
    captionWidth: 0,
    inputType: 'date'
  };
  disabled = false;

  firstColumnWidth = computed(
    () => this.sharedFirstColumnWidths()[this.firstSiblingItemId()] || getOptimalFirstColumnWidth(this.properties.text)
  );

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `date_input_${this.formControlId}`);

  currentTextColor = signal('');

  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  getTextStyle(): { [key: string]: string } | null {
    if (!this.properties.text) {
      return null;
    }

    const textStyle = getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor());

    return {
      'min-width': `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...textStyle
    };
  }

  getTextTitle(): string | null {
    if (!this.properties.text) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.text;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.parseStringToDateInput(this.properties.selectedDateTime);
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.selectedDateTime !== DEFAULT_INITIAL_DATE) {
      upsertChildren(element, { SelectedDateTime: this.properties.selectedDateTime });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & DateInputProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const selectedDateTime = props['SelectedDateTime'] || '';
    const captionWidth = Number(props['CaptionWidth'] || 0);
    const inputType = props['Type'] || 'date';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      textBgColor,
      inputColor,
      style,
      selectedDateTime,
      captionWidth,
      inputType,
      isGenericRepeatItem
    };
  }

  private parseStringToDateInput(value: string): string | null {
    if (!value || value === DEFAULT_INITIAL_DATE) {
      return null;
    }

    // Parse ISO date
    if (value.includes('-')) {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : this.formatDateForInput(date);
    }

    // Parse dot separated dates (DD.MM.YYYY)
    const [day, month, year] = value.split('.');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return this.formatDateForInput(date);
  }

  private formatDateForInput(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatDateForStorage(inputValue: string): string {
    if (!inputValue) return DEFAULT_INITIAL_DATE;

    const [year, month, day] = inputValue.split('-');
    return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
  }

  onSetTodayClick(event: Event): void {
    event.preventDefault();
    const today = new Date();
    today.setUTCFullYear(today.getFullYear(), today.getMonth(), today.getDate());
    today.setUTCHours(0, 0, 0, 0);
    const todayString = this.formatDateForInput(today);
    this.formControl?.setValue(todayString);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    // Also update the properties for XML storage
    this.properties.selectedDateTime = today.toISOString();
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.formatDateForStorage(todayString);
  }

  onClearDateClick(event: Event): void {
    event.preventDefault();
    this.formControl?.setValue(null);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    // Also update the properties for XML storage
    this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    this.properties.resultTime = new Date().toISOString();
    this.properties.resultUser = this.user().displayName;
    this.properties.resultValue = this.properties.selectedDateTime;
  }

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value) {
      // Update the underlying selectedDateTime format for XML storage
      const dateValue = new Date(value);
      this.properties.selectedDateTime = dateValue.toISOString();
      this.properties.resultTime = new Date().toISOString();
      this.properties.resultUser = this.user().displayName;
      this.properties.resultValue = this.formatDateForStorage(this.formatDateForInput(dateValue));
    } else {
      this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    }
  }
}
